import React, { useEffect, useState, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import { SlidersHorizontal, X } from "lucide-react";
import api from "@/lib/api";
import ProductCard from "@/components/ProductCard";
import Seo from "@/components/Seo";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Sheet, SheetContent, SheetTrigger, SheetHeader, SheetTitle } from "@/components/ui/sheet";

const SIZES = ["S", "M", "L", "XL", "XXL", "One Size"];
const PRICES = [["Όλες", "", ""], ["< €30", "0", "30"], ["€30 - €60", "30", "60"], ["€60 - €90", "60", "90"], ["> €90", "90", "9999"]];

export default function Shop() {
  const [params, setParams] = useSearchParams();
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [colors, setColors] = useState([]);
  const [loading, setLoading] = useState(true);

  const category = params.get("category") || "";
  const search = params.get("search") || "";
  const badge = params.get("badge") || "";
  const onSale = params.get("on_sale") || "";
  const size = params.get("size") || "";
  const color = params.get("color") || "";
  const sort = params.get("sort") || "newest";
  const priceIdx = params.get("price") || "";

  const setParam = (k, v) => {
    const next = new URLSearchParams(params);
    if (v) next.set(k, v); else next.delete(k);
    setParams(next);
  };

  const load = useCallback(async () => {
    setLoading(true);
    const q = new URLSearchParams();
    if (category) q.set("category", category);
    if (search) q.set("search", search);
    if (badge) q.set("badge", badge);
    if (onSale) q.set("on_sale", "true");
    if (size) q.set("size", size);
    if (color) q.set("color", color);
    if (sort) q.set("sort", sort);
    if (priceIdx) {
      const p = PRICES[+priceIdx];
      if (p && p[1]) q.set("min_price", p[1]);
      if (p && p[2]) q.set("max_price", p[2]);
    }
    try {
      const { data } = await api.get(`/products?${q.toString()}`);
      setProducts(data);
      const allColors = [...new Set(data.flatMap((p) => p.colors || []))];
      if (colors.length === 0) setColors(allColors);
    } finally {
      setLoading(false);
    }
  }, [category, search, badge, onSale, size, color, sort, priceIdx]); // eslint-disable-line

  useEffect(() => { load(); }, [load]);
  useEffect(() => { api.get("/categories").then(({ data }) => setCategories(data)).catch(() => {}); }, []);

  const title = badge === "new" ? "NEW DROP" : badge === "best_seller" ? "BEST SELLERS" : badge === "limited" ? "LIMITED EDITION" : onSale ? "SALE" : category || (search ? `"${search}"` : "ΟΛΑ ΤΑ ΠΡΟΪΟΝΤΑ");

  const Filters = () => (
    <div className="space-y-8">
      <div>
        <h3 className="font-display font-bold text-sm uppercase tracking-tight mb-3">Κατηγορία</h3>
        <div className="space-y-1.5">
          <button onClick={() => setParam("category", "")} className={`block text-sm ${!category ? "text-[#FF2E4C]" : "text-zinc-400 hover:text-white"}`}>Όλες</button>
          {categories.map((c) => (
            <button key={c.id} onClick={() => setParam("category", c.name)} className={`block text-sm ${category === c.name ? "text-[#FF2E4C]" : "text-zinc-400 hover:text-white"}`} data-testid={`filter-cat-${c.slug}`}>{c.name}</button>
          ))}
        </div>
      </div>
      <div>
        <h3 className="font-display font-bold text-sm uppercase tracking-tight mb-3">Τιμή</h3>
        <div className="space-y-1.5">
          {PRICES.map((p, i) => (
            <button key={i} onClick={() => setParam("price", i === 0 ? "" : String(i))} className={`block text-sm ${(priceIdx || "0") === String(i) || (i === 0 && !priceIdx) ? "text-[#FF2E4C]" : "text-zinc-400 hover:text-white"}`}>{p[0]}</button>
          ))}
        </div>
      </div>
      <div>
        <h3 className="font-display font-bold text-sm uppercase tracking-tight mb-3">Μέγεθος</h3>
        <div className="flex flex-wrap gap-2">
          {SIZES.map((s) => (
            <button key={s} onClick={() => setParam("size", size === s ? "" : s)} className={`px-3 py-1.5 text-xs font-mono border rounded ${size === s ? "bg-[#FF2E4C] border-[#FF2E4C] text-white" : "border-zinc-700 text-zinc-400 hover:border-white"}`} data-testid={`filter-size-${s}`}>{s}</button>
          ))}
        </div>
      </div>
      {colors.length > 0 && <div>
        <h3 className="font-display font-bold text-sm uppercase tracking-tight mb-3">Χρώμα</h3>
        <div className="flex flex-wrap gap-2">
          {colors.map((c) => (
            <button key={c} onClick={() => setParam("color", color === c ? "" : c)} className={`px-3 py-1.5 text-xs border rounded ${color === c ? "bg-[#FF2E4C] border-[#FF2E4C] text-white" : "border-zinc-700 text-zinc-400 hover:border-white"}`}>{c}</button>
          ))}
        </div>
      </div>}
    </div>
  );

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12 py-8 sm:py-12">
      <Seo title={title} description={`Αγόρασε ${title} από τη συλλογή SKGWEAR. Premium streetwear, EUR, αποστολή σε όλη την Ελλάδα.`} />
      <div className="mb-8">
        <p className="font-mono text-[11px] tracking-[0.3em] uppercase text-[#FF2E4C] mb-2">SKGWEAR STORE</p>
        <h1 className="font-display text-3xl sm:text-5xl font-black uppercase tracking-tighter">{title}</h1>
      </div>

      <div className="flex items-center justify-between mb-6 gap-3">
        <div className="flex items-center gap-2 flex-wrap">
          {(category || size || color || onSale || badge || priceIdx) && (
            <button onClick={() => setParams(new URLSearchParams())} className="text-xs font-mono uppercase text-zinc-400 hover:text-[#FF2E4C] flex items-center gap-1" data-testid="clear-filters"><X className="w-3 h-3" /> ΚΑΘΑΡΙΣΜΟΣ</button>
          )}
          <span className="text-xs font-mono text-zinc-500">{products.length} προϊόντα</span>
        </div>
        <div className="flex items-center gap-2">
          <Sheet>
            <SheetTrigger asChild>
              <button className="lg:hidden flex items-center gap-2 text-sm border border-zinc-700 rounded px-3 py-2" data-testid="mobile-filters-toggle"><SlidersHorizontal className="w-4 h-4" /> ΦΙΛΤΡΑ</button>
            </SheetTrigger>
            <SheetContent side="left" className="bg-[#0A0A0B] border-zinc-800 overflow-y-auto">
              <SheetHeader><SheetTitle className="font-display uppercase text-white">Φίλτρα</SheetTitle></SheetHeader>
              <div className="mt-6"><Filters /></div>
            </SheetContent>
          </Sheet>
          <Select value={sort} onValueChange={(v) => setParam("sort", v)}>
            <SelectTrigger className="w-[180px] bg-[#18181C] border-zinc-700 text-sm" data-testid="sort-select"><SelectValue /></SelectTrigger>
            <SelectContent className="bg-[#18181C] border-zinc-700 text-white">
              <SelectItem value="newest">Νεότερα</SelectItem>
              <SelectItem value="best_sellers">Best Sellers</SelectItem>
              <SelectItem value="price_asc">Τιμή: Χαμηλή → Υψηλή</SelectItem>
              <SelectItem value="price_desc">Τιμή: Υψηλή → Χαμηλή</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="flex gap-10">
        <aside className="hidden lg:block w-56 shrink-0"><div className="sticky top-28"><Filters /></div></aside>
        <div className="flex-1">
          {loading ? (
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4 sm:gap-6">
              {Array.from({ length: 6 }).map((_, i) => <div key={i} className="aspect-[3/4] bg-[#18181C] rounded-lg animate-pulse" />)}
            </div>
          ) : products.length === 0 ? (
            <div className="text-center py-24"><p className="text-zinc-400">Δεν βρέθηκαν προϊόντα.</p></div>
          ) : (
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4 sm:gap-6" data-testid="product-grid">
              {products.map((p, i) => <ProductCard key={p.id} product={p} index={i} />)}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
